import type { ReactNode } from 'react'
import type { ReferentielUpSpot } from '@/services/api'

/* Les droits d'un compte UP-SPOT, ressource par ressource (24/09/2026).
 *
 * Deux droits, indépendants :
 *   - « Réserve » : la ressource apparaît pour la personne, qui peut la réserver ;
 *   - « Gère »    : la personne reçoit les demandes de réservation de cette
 *                   ressource et les traite.
 *
 * Seuls les rôles Admin, Gestionnaire et Gestionnaire lecture peuvent gérer :
 * pour un simple Utilisateur, la colonne « Gère » est grisée. Un Admin, lui,
 * voit et réserve déjà tout — ses droits « Réserve » n'ont pas d'effet.
 */

export const ROLES_GESTIONNAIRES = ['admin', 'administrateur', 'gestionnaire', 'gestionnaire lecture']
export const peutGererSelonRole = (role: string | null | undefined) =>
  ROLES_GESTIONNAIRES.includes((role || '').toLowerCase())
export const estRoleAdmin = (role: string | null | undefined) =>
  ['admin', 'administrateur'].includes((role || '').toLowerCase())

interface EditeurProps {
  referentiel: ReferentielUpSpot
  acces: number[]
  gestion: number[]
  role: string | null
  onChange: (acces: number[], gestion: number[]) => void
}

export function EditeurDroitsUpSpot({ referentiel, acces, gestion, role, onChange }: EditeurProps) {
  const gestionPossible = peutGererSelonRole(role)
  const a = new Set(acces)
  const g = new Set(gestion)

  const basculer = (ensemble: Set<number>, id: number) => {
    const suivant = new Set(ensemble)
    if (suivant.has(id)) suivant.delete(id)
    else suivant.add(id)
    return [...suivant]
  }

  if (referentiel.categories.length === 0) {
    return <p className="text-sm text-slate-500">UP-SPOT ne compte encore aucune ressource.</p>
  }

  return (
    <div className="space-y-3">
      {estRoleAdmin(role) && (
        <p className="text-xs text-slate-500">
          Un administrateur voit et réserve déjà toutes les ressources. Seule la colonne « Gère » a un effet pour lui.
        </p>
      )}
      {referentiel.categories.map((categorie) => {
        const ids = categorie.ressources.map((r) => r.id)
        const toutes = ids.length > 0 && ids.every((id) => a.has(id))
        return (
          <div key={categorie.id} className="rounded-xl border border-violet-100 bg-white">
            <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-violet-50">
              <span className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: categorie.couleur || '#a78bfa' }}
                />
                {categorie.nom}
              </span>
              {ids.length > 0 && (
                <button
                  type="button"
                  className="text-xs font-medium text-violet-700 hover:underline"
                  onClick={() => {
                    const suivant = new Set(a)
                    ids.forEach((id) => (toutes ? suivant.delete(id) : suivant.add(id)))
                    onChange([...suivant], [...g])
                  }}
                >
                  {toutes ? 'Ne rien réserver' : 'Tout réserver'}
                </button>
              )}
            </div>
            <div className="divide-y divide-violet-50">
              {categorie.ressources.map((r) => (
                <div key={r.id} className="flex items-center gap-3 px-3 py-1.5 text-sm">
                  <span className="flex-1 min-w-0 truncate text-slate-700" title={r.nom}>
                    {r.nom}
                    {r.statut === 'Indisponible' && <span className="ml-1 text-xs text-slate-400">(indisponible)</span>}
                  </span>
                  <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-violet-600"
                      checked={a.has(r.id)}
                      onChange={() => onChange(basculer(a, r.id), [...g])}
                    />
                    Réserve
                  </label>
                  <label
                    className={`flex items-center gap-1.5 text-xs ${gestionPossible ? 'text-slate-600 cursor-pointer' : 'text-slate-300 cursor-not-allowed'}`}
                    title={gestionPossible ? undefined : 'Un simple Utilisateur ne peut pas gérer de ressource.'}
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-violet-600"
                      disabled={!gestionPossible}
                      checked={gestionPossible && g.has(r.id)}
                      onChange={() => onChange([...a], basculer(g, r.id))}
                    />
                    Gère
                  </label>
                </div>
              ))}
              {categorie.ressources.length === 0 && (
                <p className="px-3 py-1.5 text-xs text-slate-400">Aucune ressource dans cette catégorie.</p>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

interface ResumeProps {
  referentiel: ReferentielUpSpot | null
  acces: number[]
  gestion: number[]
  role: string | null
}

/** Les droits en lecture : les noms des ressources, pas des cases. */
export function ResumeDroitsUpSpot({ referentiel, acces, gestion, role }: ResumeProps) {
  const nomDe = (id: number) =>
    referentiel?.categories.flatMap((c) => c.ressources).find((r) => r.id === id)?.nom ?? `ressource n° ${id}`
  const liste = (ids: number[]) =>
    ids.length === 0 ? <span className="text-slate-400">aucune</span> : ids.map(nomDe).join(', ')

  /* Libellé à largeur fixe, valeur qui revient à la ligne dans sa colonne :
     une longue liste de ressources reste alignée au lieu de repasser sous le
     libellé. */
  const ligne = (libelle: string, valeur: ReactNode) => (
    <p className="flex gap-2">
      <span className="text-xs text-slate-400 w-24 shrink-0 pt-0.5">{libelle}</span>
      <span className="flex-1 min-w-0 text-slate-800">{valeur}</span>
    </p>
  )

  return (
    <div className="space-y-1 text-sm">
      {ligne('Réserve', estRoleAdmin(role) ? 'toutes les ressources (administrateur)' : liste(acces))}
      {(peutGererSelonRole(role) || gestion.length > 0) && ligne('Gère', liste(gestion))}
    </div>
  )
}
